"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Bar, BarChart, CartesianGrid, LabelList,
  XAxis, YAxis, Legend, Tooltip, ResponsiveContainer,
} from "recharts";
import { CircleHelp, Download, ChevronDown } from "lucide-react";
import {
  Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import axiosInstance from "@/api/axios";
import { ChartContainer } from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const round3 = (v) => Math.round(v * 1000) / 1000;

const normalise = (ws) => {
  const l = (ws || "").toLowerCase();
  if (l === "organic")                  return "Organic";
  if (l === "pmd" || l === "plastic")   return "PMD";
  if (l === "paper")                    return "Paper";
  if (l === "residual" || l === "rest") return "Residual";
  return null;
};

const STREAM_COLORS = {
  Organic:  "#A3176E",
  PMD:      "#436DBC",
  Paper:    "#E4981B",
  Residual: "#E53935",
};
const STREAMS = ["Organic", "PMD", "Paper", "Residual"];

const pad = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
// ISO datetime string for hourly bucket API params
const toISOHour = (d, h) =>
  `${toISO(d)}T${pad(Math.min(h, 23))}:00:00`;

// Build date-range buckets — one API call per bucket
const buildPeriods = (timeRange, customDateRange, isCustomDateRange) => {
  const now = new Date();

  if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
    const s    = new Date(customDateRange[0].startDate);
    const e    = new Date(customDateRange[0].endDate);
    const days = Math.round((e - s) / 86400000);

    // Single day selected → 24 hourly buckets
    if (days === 0) {
      return Array.from({ length: 24 }, (_, h) => ({
        label:      `${pad(h)}:00`,
        start:      toISOHour(s, h),
        end:        toISOHour(s, h === 23 ? 23 : h + 1),
        hourBucket: true,
      }));
    }

    if (days <= 35) {
      const periods = [];
      for (let i = 0; i <= days; i++) {
        const d = new Date(s); d.setDate(d.getDate() + i);
        periods.push({
          label: `${pad(d.getDate())} ${d.toLocaleString("en", { month: "short" })}`,
          start: toISO(d),
          end:   toISO(d),
        });
      }
      return periods;
    }

    const periods = [];
    const cur = new Date(s.getFullYear(), s.getMonth(), 1);
    while (cur <= e) {
      const mEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      periods.push({
        label: cur.toLocaleString("en", { month: "short", year: "2-digit" }),
        start: toISO(cur),
        end:   toISO(mEnd > e ? e : mEnd),
      });
      cur.setMonth(cur.getMonth() + 1);
    }
    return periods;
  }

  // Normalise: collapse all whitespace/underscores, lowercase
  // Handles "3 month", "3month", "3_month", "3Month", etc.
  const tr = (timeRange || "year").replace(/[\s_-]+/g, "").toLowerCase();

  if (tr === "day") {
    // Today → 24 hourly buckets
    return Array.from({ length: 24 }, (_, h) => ({
      label:      `${pad(h)}:00`,
      start:      toISOHour(now, h),
      end:        toISOHour(now, h === 23 ? 23 : h + 1),
      hourBucket: true,
    }));
  }

  if (tr === "week") {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now); d.setDate(d.getDate() - (6 - i));
      return {
        label: d.toLocaleString("en", { weekday: "short" }) + " " + pad(d.getDate()),
        start: toISO(d),
        end:   toISO(d),
      };
    });
  }

  if (tr === "month") {
    const periods = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now); d.setDate(d.getDate() - i);
      periods.push({
        label: `${pad(d.getDate())} ${d.toLocaleString("en", { month: "short" })}`,
        start: toISO(d),
        end:   toISO(d),
      });
    }
    return periods;
  }

  // 3month / 6month / year — all use monthly buckets, just different count
  const monthCount = tr === "3month" ? 3 : tr === "6month" ? 6 : 12;
  const periods = [];
  for (let i = monthCount - 1; i >= 0; i--) {
    const d    = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const mEnd = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    periods.push({
      label: d.toLocaleString("en", { month: "short", year: "2-digit" }),
      start: toISO(d),
      end:   toISO(mEnd),
    });
  }
  return periods;
};

const CustomTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload?.length) return null;
  const overall    = payload[0]?.payload?.contamination_rate;
  const totalKg    = payload[0]?.payload?.total_contaminated_kg;
  const totalItems = payload[0]?.payload?.total_items;
  return (
    <div className="rounded-lg border bg-background p-3 shadow-md text-sm min-w-[190px]">
      <p className="font-semibold mb-2">{label}</p>
      <div className="space-y-1">
        {payload.filter((e) => Number(e.value) > 0).map((entry, i) => (
          <div key={i} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.fill }} />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
            <span className="font-medium">
              {mode === "kg"
                ? `${Number(entry.value).toFixed(3)} kg`
                : `${Number(entry.value).toFixed(1)}%`}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t mt-2 pt-2 text-xs text-muted-foreground space-y-0.5">
        {mode === "percentage" && overall != null && (
          <div>Overall contamination: <span className="font-medium text-foreground">{Number(overall).toFixed(1)}%</span></div>
        )}
        {mode === "kg" && totalKg != null && (
          <div>Total contaminated: <span className="font-medium text-foreground">{Number(totalKg).toFixed(3)} kg</span></div>
        )}
        {totalItems != null && totalItems > 0 && (
          <div>Total items: <span className="font-medium text-foreground">{totalItems}</span></div>
        )}
      </div>
    </div>
  );
};

export function ContaminationTimeSeries({
  className,
  title,
  bins = [],
  timeRange,
  customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
  isCustomDateRange = false,
  ...props
}) {
  const exportRef = useRef(null);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState(null);
  const [mode, setMode]           = useState("percentage"); // "percentage" | "kg"

  const chartConfig = {
    Organic:  { label: "Organic",  color: STREAM_COLORS.Organic  },
    PMD:      { label: "PMD",      color: STREAM_COLORS.PMD      },
    Paper:    { label: "Paper",    color: STREAM_COLORS.Paper     },
    Residual: { label: "Residual", color: STREAM_COLORS.Residual  },
  };

useEffect(() => {
  if (!bins || bins.length === 0) return;
  if (!timeRange && !isCustomDateRange) return;

  setIsLoading(true);
  setError(null);
  setChartData([]);

  const p = new URLSearchParams();
  bins.forEach((b) => p.append("bin", b));
  p.set("period", timeRange || "year");

  if (isCustomDateRange && customDateRange?.[0]?.startDate && customDateRange?.[0]?.endDate) {
    const s = customDateRange[0].startDate;
    const e = customDateRange[0].endDate;
    p.set("start_date", s instanceof Date ? toISO(s) : toISO(new Date(s)));
    p.set("end_date",   e instanceof Date ? toISO(e) : toISO(new Date(e)));
  }

  axiosInstance
    .get(`/contamination-timeseries/?${p.toString()}`)
    .then((res) => {
      const data = res.data;
      if (!Array.isArray(data)) { setError("Unexpected response"); return; }

      const shaped = data.map((item) => {
        const overall = item.contamination_rate ?? 0;
        const rawSum  = (item.organic_rate ?? 0) + (item.pmd_rate ?? 0) +
                        (item.paper_rate  ?? 0) + (item.residual_rate ?? 0);

        const scale = rawSum > 0 ? overall / rawSum : 0;

        return {
          period:                item.period,
          contamination_rate:    overall,
          total_contaminated_kg: item.total_contaminated_kg ?? 0,
          total_items:           item.total_items           ?? 0,
          Organic_pct:  (item.organic_rate  ?? 0) * scale,
          PMD_pct:      (item.pmd_rate      ?? 0) * scale,
          Paper_pct:    (item.paper_rate    ?? 0) * scale,
          Residual_pct: (item.residual_rate ?? 0) * scale,
          Organic_kg:   item.organic_kg  ?? 0,
          PMD_kg:       item.pmd_kg      ?? 0,
          Paper_kg:     item.paper_kg    ?? 0,
          Residual_kg:  item.residual_kg ?? 0,
        };
      });

      setChartData(shaped);
    })
    .catch(() => setError("Failed to load data"))
    .finally(() => setIsLoading(false));
}, [
  timeRange,
  isCustomDateRange,
  JSON.stringify(bins),
  customDateRange?.[0]?.startDate instanceof Date
    ? customDateRange[0].startDate.toDateString()
    : String(customDateRange?.[0]?.startDate ?? ""),
  customDateRange?.[0]?.endDate instanceof Date
    ? customDateRange[0].endDate.toDateString()
    : String(customDateRange?.[0]?.endDate ?? ""),
]);
  const suffix        = mode === "kg" ? "_kg" : "_pct";
  const activeStreams = STREAMS.filter((s) => chartData.some((d) => (d[`${s}${suffix}`] ?? 0) > 0));

  // Dynamic Y-axis upper bound: max value + 15% breathing room, rounded up to nearest 5
  const yMax = (() => {
    if (mode === "percentage") return 100;
    if (!chartData.length) return "auto";
    const max = Math.max(...chartData.map((d) =>
      STREAMS.reduce((sum, s) => sum + (d[`${s}_kg`] ?? 0), 0)
    ));
    if (!max) return "auto";
    const padded = max * 1.15;
    return Math.ceil(padded / 5) * 5;
  })();

  const handleExportCSV = () => {
    if (!chartData.length) return;
    const headers = [
      "Period", "Overall %", "Total Contaminated kg", "Total Items",
      "Organic %", "PMD %", "Paper %", "Residual %",
      "Organic kg", "PMD kg", "Paper kg", "Residual kg",
    ];
    const rows = chartData.map((d) =>
      [
        d.period,
        d.contamination_rate?.toFixed(2)   ?? "",
        d.total_contaminated_kg?.toFixed(3) ?? "",
        d.total_items ?? "",
        d.Organic_pct?.toFixed(2)  ?? "", d.PMD_pct?.toFixed(2)  ?? "",
        d.Paper_pct?.toFixed(2)    ?? "", d.Residual_pct?.toFixed(2) ?? "",
        d.Organic_kg?.toFixed(3)   ?? "", d.PMD_kg?.toFixed(3)   ?? "",
        d.Paper_kg?.toFixed(3)     ?? "", d.Residual_kg?.toFixed(3) ?? "",
      ].join(",")
    );
    const blob = new Blob([[headers.join(","), ...rows].join("\n")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href     = URL.createObjectURL(blob);
    link.download = `contamination_timeseries_${timeRange || "data"}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleExportPNG = () => {
    if (!exportRef.current) return;
    const controls = exportRef.current.querySelector(".export-controls");
    if (controls) controls.style.display = "none";
    import("html2canvas").then((h) => {
      h.default(exportRef.current, { scale: 2, useCORS: true }).then((canvas) => {
        const link = document.createElement("a");
        link.download = `contamination_timeseries_${timeRange || "data"}.png`;
        link.href     = canvas.toDataURL("image/png");
        link.click();
      }).finally(() => { if (controls) controls.style.display = ""; });
    });
  };

  return (
    <div
      ref={exportRef}
      className={cn(
        "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section border-0 shadow-none",
        className
      )}
      {...props}
    >
      <div className="flex flex-wrap justify-between items-start gap-2 pb-2">
        <div>
          <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
            {title || "Contamination Rate Over Time"}
          </h4>
          {isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {new Date(customDateRange[0].startDate).toLocaleDateString()} –{" "}
              {new Date(customDateRange[0].endDate).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 export-controls">
          {/* KG / % toggle */}
          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
            <button
              onClick={() => setMode("percentage")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "percentage"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >%</button>
            <button
              onClick={() => setMode("kg")}
              className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${
                mode === "kg"
                  ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >KG</button>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm"
                      className="text-muted-foreground hover:text-primary"
                      disabled={!chartData.length || isLoading}>
                <Download className="h-4 w-4 mr-1" />
                Export
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV}>Download as CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportPNG}>Download as PNG</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger asChild>
                <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[220px]">
                {mode === "percentage"
                  ? "Bar height = actual overall contamination %. Segments show each stream's share."
                  : "Bar height = real contaminated kg per stream, directly from the scale data."}
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>
      </div>

      <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: "100%", height: "100%" }}>
        {isLoading ? (
          <div className="flex items-center justify-center h-full w-full">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-destructive">{error}</div>
        ) : chartData.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[400px]">
            <div className="text-muted-foreground text-center">
              <p className="mb-2 text-lg">No data available</p>
              <p className="text-sm">Select a time range and bins to view contamination trends</p>
            </div>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-full w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                // left margin increased so label text above bars has breathing room
                // and bars don't hug the Y-axis
                margin={{ top: 28, right: 30, left: 8, bottom: 20 }}
                barCategoryGap="20%"
                // padding pushes the first/last bar slightly away from the axis edges
                barGap={4}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  interval="preserveStartEnd"
                  // padding so first/last bars don't sit right against the axis
                  padding={{ left: 16, right: 16 }}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={{ stroke: "hsl(var(--border))" }}
                  axisLine={{ stroke: "hsl(var(--border))" }}
                  tickFormatter={(v) => mode === "kg" ? `${v} kg` : `${v}%`}
                  domain={mode === "percentage" ? [0, 100] : [0, yMax]}
                  ticks={mode === "percentage" ? [0, 20, 40, 60, 80, 100] : undefined}
                  width={mode === "kg" ? 64 : 44}
                />
                <Tooltip content={<CustomTooltip mode={mode} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} />
                <Legend
                  verticalAlign="bottom"
                  iconType="square"
                  iconSize={10}
                  wrapperStyle={{ fontSize: 12, paddingTop: 14 }}
                />

                {activeStreams.map((stream, idx) => (
                  <Bar
                    key={stream}
                    dataKey={`${stream}${suffix}`}
                    stackId="contamination"
                    fill={STREAM_COLORS[stream]}
                    name={stream}
                    maxBarSize={60}
                    stroke="white"
                    strokeWidth={1}
                    radius={idx === activeStreams.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  >
    
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </div>
    </div>
  );
}

export default ContaminationTimeSeries;